import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Checkbox,
  TextField,
  InputAdornment,
  CircularProgress,
  Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

/* ─── small style helpers ─── */
const headerSx = {
  '& th': {
    bgcolor: 'grey.100',
    color: 'text.primary',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    p: 1.25,
  },
};
const cellSx = { p: 1.25 };

export default function SearchableSelectableTable({
  queryFn,
  columns,
  onSelectionChange,
  toolbar,
  filterFn = null,
}) {
  /* local state */
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState({});

  /* safe key & fetcher */
  const safeKey = typeof queryFn === 'function' && queryFn.name
    ? ['rows', queryFn.name]
    : ['rows'];

  const safeFetch = async () => {
    if (typeof queryFn === 'function') return queryFn();
    console.warn('[SearchableSelectableTable] No queryFn supplied');
    return [];
  };

  const { data = [], isFetching } = useQuery(safeKey, safeFetch, {
    refetchOnWindowFocus: false,
  });

  /* derive rows (apply search / filter) */
  const rows = useMemo(() => {
    const q = search.toLowerCase();
    const base = Array.isArray(data) ? data : [];

    const passes = (r) =>
      columns.some((c) =>
        String(r[c.key] ?? '')
          .toLowerCase()
          .includes(q)
      ) ||
      `${r.first_name ?? ''} ${r.last_name ?? ''}`.toLowerCase().includes(q);

    return filterFn ? base.filter((r) => filterFn(r, q)) : base.filter(passes);
  }, [data, search, columns, filterFn]);

  /* selection helpers */
  const rowId   = (r, idx) => r.id ?? r._id ?? idx; // robust ID
  const emitSel = (sel) => onSelectionChange?.(rows.filter((r, i) => sel[rowId(r, i)]));

  const toggle = (rid) => {
    const next = { ...selected, [rid]: !selected[rid] };
    setSelected(next);
    emitSel(next);
  };

  const toggleAll = (checked) => {
    const next = Object.fromEntries(rows.map((r, i) => [rowId(r, i), checked]));
    setSelected(next);
    emitSel(next);
  };

  const allChecked  = rows.length && rows.every((r, i) => selected[rowId(r, i)]);
  const someChecked = rows.some((r, i) => selected[rowId(r, i)]) && !allChecked;

  /* loading spinner */
  if (isFetching) {
    return (
      <Paper elevation={2} sx={{ p: 4 }}>
        {toolbar}
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  /* render */
  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      {toolbar}
      <TextField
        fullWidth
        placeholder="Search…"
        size="small"
        sx={{ mb: 2 }}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead sx={headerSx}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={someChecked}
                  checked={allChecked}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
              </TableCell>
              {columns.map((c) => (
                <TableCell key={c.key}>{c.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((r, idx) => {
              const rid = rowId(r, idx);
              return (
                <TableRow key={rid} hover selected={!!selected[rid]}>
                  <TableCell padding="checkbox" sx={cellSx}>
                    <Checkbox
                      checked={!!selected[rid]}
                      onChange={() => toggle(rid)}
                    />
                  </TableCell>

                  {columns.map((c) => (
                    <TableCell key={c.key} sx={cellSx}>
                      {typeof c.render === 'function'
                        ? c.render(r)
                        : r[c.key]}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}

            {!rows.length && (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  No results
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
